import { useTranslation } from 'react-i18next';
import { FileText, File, Trash2, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import type { TaskAttachment } from '../../types';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentCardProps {
  attachment: TaskAttachment;
  onDelete: (id: string) => void;
}

export function AttachmentCard({ attachment, onDelete }: AttachmentCardProps) {
  const { t } = useTranslation('tasks');
  const isImage = attachment.type === 'image';

  return (
    <div
      data-testid={`tasks-attachment-card-${attachment.id}`}
      className="group relative flex flex-col overflow-hidden rounded-lg border bg-card transition-colors hover:border-primary/30"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        {isImage && attachment.thumbnailUrl ? (
          <img
            src={attachment.thumbnailUrl}
            alt={attachment.name}
            loading="lazy"
            className="size-full object-cover"
            data-testid={`tasks-attachment-thumb-${attachment.id}`}
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            {attachment.type === 'document' ? (
              <FileText className="size-8 text-muted-foreground" />
            ) : (
              <File className="size-8 text-muted-foreground" />
            )}
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('attachments.openAria')}
                data-testid={`tasks-attachment-open-${attachment.id}`}
                className="rounded-full bg-background/80 p-2 text-foreground hover:bg-background"
              >
                <ExternalLink className="size-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent>{t('attachments.openAria')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onDelete(attachment.id)}
                aria-label={t('attachments.deleteAria')}
                data-testid={`tasks-attachment-delete-${attachment.id}`}
                className="rounded-full bg-background/80 p-2 text-destructive hover:bg-background"
              >
                <Trash2 className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('attachments.deleteAria')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="px-2 py-1.5">
        <p className="truncate text-xs font-medium" title={attachment.name}>
          {attachment.name}
        </p>
        <p className="text-[10px] text-muted-foreground">{formatBytes(attachment.size)}</p>
      </div>
    </div>
  );
}
